import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X, Search, Loader2, ChevronDown, ChevronRight, AlertTriangle, Save, HelpCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { posApi } from '@/services/api';
import { formatMoney } from '@/utils/currency';
import { patchLocalItemPrices } from '@/offline/repositories';
import { useBulkItemSelection, type BulkSelectableRow } from '@/hooks/useBulkItemSelection';
import type { POSItem } from '@/types';

interface Props {
  onClose: () => void;
  onPrintLabels?: (rows: BulkSelectableRow[]) => void;
}

type PriceField = 'sale' | 'wholesale' | 'reseller' | 'minimum' | 'delivery';
type PriceEdits = Record<string, Partial<Record<PriceField, number | string>>>;
type RuleBaseField = 'sale' | 'current' | 'cost' | 'wholesale' | 'reseller' | 'minimum' | 'delivery';

const ALL_PRICE_FIELDS: PriceField[] = ['sale', 'wholesale', 'reseller', 'minimum', 'delivery'];
const BASE_PRICE_FIELDS: PriceField[] = ['sale', 'wholesale', 'reseller', 'minimum'];

const BULK_RULE_HELP_KEYS = [
  { labelKey: 'items.rulePercent', helpKey: 'items.rulePercentHelp' },
  { labelKey: 'items.ruleAmount', helpKey: 'items.ruleAmountHelp' },
  { labelKey: 'items.ruleSet', helpKey: 'items.ruleSetHelp' },
  { labelKey: 'items.ruleMargin', helpKey: 'items.ruleMarginHelp' },
] as const;

function decimalPlaces(step: number): number {
  if (step >= 1) return 0;
  const s = step.toString();
  const dot = s.indexOf('.');
  return dot === -1 ? 0 : s.length - dot - 1;
}

function roundPrice(value: number, step: number): number {
  let rounded = value;
  if (step > 0) {
    rounded = Math.round(value / step) * step;
  }
  const dp = step > 0 ? decimalPlaces(step) : 2;
  return Number(rounded.toFixed(dp));
}

function getOldPrice(row: BulkSelectableRow, field: PriceField): number {
  if (field === 'sale') return row.salePrice;
  if (field === 'wholesale') return row.wholesalePrice;
  if (field === 'reseller') return row.resellerPrice;
  if (field === 'minimum') return row.minimumPrice;
  return row.deliveryPrice || 0;
}

function getNewPrice(row: BulkSelectableRow, edits: PriceEdits, field: PriceField): number {
  const edited = edits[row.key]?.[field];
  if (edited != null && edited !== '') {
    const num = typeof edited === 'number' ? edited : parseFloat(edited);
    if (!Number.isNaN(num)) return num;
  }
  return getOldPrice(row, field);
}

function getRowBasePrice(
  row: BulkSelectableRow,
  edits: PriceEdits,
  targetField: PriceField,
  baseField: RuleBaseField,
): number {
  if (baseField === 'current') {
    return getNewPrice(row, edits, targetField);
  }
  if (baseField === 'cost') {
    return row.costPrice || 0;
  }
  return getNewPrice(row, edits, baseField as PriceField);
}

function rowHasChanges(row: BulkSelectableRow, edits: PriceEdits, fields: PriceField[]): boolean {
  const e = edits[row.key];
  if (!e) return false;
  return fields.some((f) => {
    const raw = e[f];
    if (raw == null || raw === '') return false;
    const num = typeof raw === 'number' ? raw : parseFloat(raw);
    return !Number.isNaN(num) && num !== getOldPrice(row, f);
  });
}

export default function BulkPriceUpdateModal({ onClose, onPrintLabels }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sel = useBulkItemSelection('Product');
  const [edits, setEdits] = useState<PriceEdits>({});
  const [showReview, setShowReview] = useState(false);
  const [ruleType, setRuleType] = useState<'percent' | 'amount' | 'set' | 'margin'>('percent');
  const [ruleValue, setRuleValue] = useState('');
  const [ruleBaseField, setRuleBaseField] = useState<RuleBaseField>('sale');
  const [ruleTarget, setRuleTarget] = useState<'sale' | 'wholesale' | 'reseller' | 'minimum' | 'delivery' | 'all'>('sale');
  const [roundToStep, setRoundToStep] = useState('0.05');
  const [ruleApplyKeys, setRuleApplyKeys] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const { data: posSettings } = useQuery({
    queryKey: ['posSettings'],
    queryFn: async () => {
      try {
        const res = await posApi.getPosSettings();
        return res.data?.message;
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });

  const showDeliveryPrice = Boolean(posSettings?.enable_delivery);

  const priceFields: PriceField[] = useMemo(() => {
    return showDeliveryPrice ? ALL_PRICE_FIELDS : BASE_PRICE_FIELDS;
  }, [showDeliveryPrice]);

  const editGridCols = showDeliveryPrice
    ? 'grid-cols-[2.5rem_minmax(0,1.4fr)_repeat(5,minmax(5rem,1fr))]'
    : 'grid-cols-[2.5rem_minmax(0,1.4fr)_repeat(4,minmax(5rem,1fr))]';

  useEffect(() => {
    setRuleApplyKeys(new Set(sel.selectedRows.map((row) => row.key)));
  }, [sel.selectedRows]);

  const changedRows = useMemo(
    () => sel.selectedRows.filter((row) => rowHasChanges(row, edits, priceFields)),
    [sel.selectedRows, edits, priceFields],
  );

  const applyRuleToField = (base: number, cost: number): number => {
    const val = parseFloat(ruleValue);
    if (Number.isNaN(val)) return base;
    let next = base;
    if (ruleType === 'set') next = val;
    else if (ruleType === 'percent') next = base * (1 + val / 100);
    else if (ruleType === 'amount') next = base + val;
    else if (ruleType === 'margin') next = cost > 0 ? cost * (1 + val / 100) : base;
    const step = parseFloat(roundToStep) || 0;
    return Math.max(0, roundPrice(next, step));
  };

  const applyRule = () => {
    const val = parseFloat(ruleValue);
    if (Number.isNaN(val)) {
      toast.error(t('items.invalidRuleValue'));
      return;
    }
    const targets: PriceField[] = ruleTarget === 'all' ? priceFields : [ruleTarget];
    let zeroBaseCount = 0;
    let modifiedCount = 0;

    setEdits((prev) => {
      const next = { ...prev };
      for (const row of sel.selectedRows) {
        if (!ruleApplyKeys.has(row.key)) continue;
        const rowEdits = { ...next[row.key] };
        for (const field of targets) {
          const base = ruleType === 'margin'
            ? (row.costPrice || 0)
            : getRowBasePrice(row, prev, field, ruleBaseField);
          if (base === 0 && (ruleType === 'percent' || ruleType === 'margin')) {
            zeroBaseCount++;
          }
          const calculated = applyRuleToField(base, row.costPrice);
          rowEdits[field] = calculated;
          if (calculated !== getOldPrice(row, field)) {
            modifiedCount++;
          }
        }
        next[row.key] = rowEdits;
      }
      return next;
    });

    if (modifiedCount === 0 && zeroBaseCount > 0) {
      if (ruleBaseField === 'cost' || ruleType === 'margin') {
        toast.error(
          t('items.zeroCostWarning', 'Cost Price is 0 for these products. Please select "Retail Price" under "Calculate from" to calculate from the prices shown in the table.'),
          { duration: 6000 },
        );
      } else {
        toast.error(
          t('items.zeroBaseWarning', 'The base price is 0 for these products. Please select a field that has a price.'),
          { duration: 5000 },
        );
      }
    } else if (zeroBaseCount > 0) {
      toast.success(t('items.ruleApplied') + ` (${zeroBaseCount} items skipped: base price was 0)`);
    } else {
      toast.success(t('items.ruleApplied'));
    }
  };

  const setFieldEdit = (row: BulkSelectableRow, field: PriceField, raw: string) => {
    setEdits((prev) => ({
      ...prev,
      [row.key]: { ...prev[row.key], [field]: raw },
    }));
  };

  const handleFieldBlur = (row: BulkSelectableRow, field: PriceField) => {
    const raw = edits[row.key]?.[field];
    if (raw === undefined) return;
    if (raw === '') {
      setEdits((prev) => {
        const next = { ...prev };
        if (next[row.key]) {
          const nextRow = { ...next[row.key] };
          delete nextRow[field];
          next[row.key] = nextRow;
        }
        return next;
      });
      return;
    }
    const v = typeof raw === 'number' ? raw : parseFloat(raw);
    if (Number.isNaN(v)) {
      setEdits((prev) => {
        const next = { ...prev };
        if (next[row.key]) {
          const nextRow = { ...next[row.key] };
          delete nextRow[field];
          next[row.key] = nextRow;
        }
        return next;
      });
    } else {
      const step = parseFloat(roundToStep) || 0;
      setEdits((prev) => ({
        ...prev,
        [row.key]: { ...prev[row.key], [field]: roundPrice(v, step) },
      }));
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = changedRows.map((row) => {
        const step = parseFloat(roundToStep) || 0;
        const sale = roundPrice(getNewPrice(row, edits, 'sale'), step);
        const wholesale = roundPrice(getNewPrice(row, edits, 'wholesale'), step);
        const reseller = roundPrice(getNewPrice(row, edits, 'reseller'), step);
        const minimum = roundPrice(getNewPrice(row, edits, 'minimum'), step);
        const delivery = showDeliveryPrice ? roundPrice(getNewPrice(row, edits, 'delivery'), step) : undefined;
        return {
          name: row.item.name,
          ...(row.variant ? { variant: row.variant.name } : {}),
          ...(sale !== row.salePrice ? { sale_price: sale } : {}),
          ...(wholesale !== row.wholesalePrice ? { wholesale_price: wholesale } : {}),
          ...(reseller !== row.resellerPrice ? { reseller_price: reseller } : {}),
          ...(minimum !== row.minimumPrice ? { minimum_price: minimum } : {}),
          ...(delivery !== undefined && delivery !== (row.deliveryPrice || 0) ? { delivery_price: delivery } : {}),
        };
      });
      const res = await posApi.bulkUpdateItemPrices(payload);
      return res.data.message as { updated_count: number; errors: { message: string }[] };
    },
    onSuccess: async (data) => {
      if (data.errors?.length) {
        toast.error(data.errors[0]?.message || t('items.priceUpdatePartialFail'));
      } else {
        toast.success(t('items.priceUpdateSuccess', { count: data.updated_count }));
      }
      await patchLocalItemPrices(
        changedRows.map((row) => {
          const e = edits[row.key] || {};
          const num = (v: number | string | undefined) => {
            if (v == null || v === '') return undefined;
            const n = typeof v === 'number' ? v : parseFloat(v);
            return Number.isNaN(n) ? undefined : n;
          };
          return {
            name: row.item.name,
            variant: row.variant?.name,
            sale_price: num(e.sale),
            wholesale_price: num(e.wholesale),
            reseller_price: num(e.reseller),
            minimum_price: num(e.minimum),
            delivery_price: num(e.delivery),
          };
        }),
      );
      void qc.invalidateQueries({ queryKey: ['items'] });
      void qc.invalidateQueries({ queryKey: ['bulkItemSelection'] });
      void qc.invalidateQueries({ queryKey: ['bulkPrintItems'] });
      if (onPrintLabels && changedRows.length > 0) {
        onPrintLabels(changedRows);
      }
      onClose();
    },
    onError: () => toast.error(t('items.priceUpdateFail')),
  });

  const handleSave = () => {
    if (changedRows.length === 0) {
      toast.error(t('items.noPriceChanges'));
      return;
    }
    const bigJump = changedRows.some((row) => {
      const oldP = row.salePrice || 1;
      const newP = getNewPrice(row, edits, 'sale');
      return Math.abs(newP - oldP) / oldP > 0.2;
    });
    if (bigJump) {
      setShowConfirmModal(true);
      return;
    }
    saveMutation.mutate();
  };

  const priceFieldLabel = (field: PriceField) => {
    if (field === 'sale') return t('items.retailPrice');
    if (field === 'wholesale') return t('items.wholesalePrice');
    if (field === 'reseller') return t('items.resellerPrice');
    if (field === 'minimum') return t('items.minimumPrice');
    return t('items.deliveryPrice');
  };

  const toggleRuleApplyKey = (key: string) => {
    setRuleApplyKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allRuleApplyChecked = sel.selectedRows.length > 0 && sel.selectedRows.every((row) => ruleApplyKeys.has(row.key));
  const someRuleApplyChecked = sel.selectedRows.some((row) => ruleApplyKeys.has(row.key));

  const toggleAllRuleApply = () => {
    if (allRuleApplyChecked) {
      setRuleApplyKeys(new Set());
    } else {
      setRuleApplyKeys(new Set(sel.selectedRows.map((row) => row.key)));
    }
  };

  const renderSelectRow = (row: BulkSelectableRow) => {
    const checked = sel.selectedKeys.has(row.key);
    return (
      <label
        key={row.key}
        className={`w-full flex items-center gap-2 px-3 py-2.5 border-b border-gray-50 cursor-pointer transition-colors hover:bg-gray-50 ${checked ? 'bg-primary-50/60' : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={() => sel.toggleKey(row.key)}
          className="w-4 h-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="flex-1 min-w-0 text-sm font-medium text-gray-900 truncate">{row.label}</span>
        <span className="text-sm text-gray-600 shrink-0">{formatMoney(row.salePrice)}</span>
      </label>
    );
  };

  const renderEditRow = (row: BulkSelectableRow) => (
    <div key={row.key} className={`grid ${editGridCols} gap-2 px-3 py-2 border-b border-gray-50 items-center`}>
      <input
        type="checkbox"
        checked={ruleApplyKeys.has(row.key)}
        onChange={() => toggleRuleApplyKey(row.key)}
        className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        aria-label={row.label}
      />
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{row.label}</p>
        <p className="text-xs text-gray-500 truncate">
          {row.categoryName} • {t('items.costPrice')}: {formatMoney(row.costPrice)}
        </p>
      </div>
      {priceFields.map((field) => {
        const oldVal = getOldPrice(row, field);
        const rawEdit = edits[row.key]?.[field];
        const valToDisplay = rawEdit !== undefined ? rawEdit : roundPrice(oldVal, parseFloat(roundToStep) || 0);
        const numericVal = getNewPrice(row, edits, field);
        const changed = rawEdit !== undefined && numericVal !== roundPrice(oldVal, parseFloat(roundToStep) || 0);
        return (
          <input
            key={field}
            type="number"
            min={0}
            step={roundToStep === '0' ? '0.01' : roundToStep}
            title={priceFieldLabel(field)}
            className={`input-field w-full text-sm py-1.5 min-h-[40px] ${changed ? 'border-amber-400 bg-amber-50' : ''}`}
            value={valToDisplay}
            onChange={(e) => setFieldEdit(row, field, e.target.value)}
            onBlur={() => handleFieldBlur(row, field)}
          />
        );
      })}
    </div>
  );

  const renderItemRow = (item: POSItem) => {
    const expanded = !!sel.expandedItems[item.name];
    const variants = sel.loadedVariants[item.name];
    const loading = !!sel.loadingVariants[item.name];

    if (item.has_variants) {
      return (
        <div key={item.name} className="border-b border-gray-100">
          <button
            type="button"
            onClick={() => void sel.toggleExpand(item)}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-50 text-left"
          >
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <span className="text-sm font-medium text-gray-900 flex-1 truncate">{item.item_name}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
          </button>
          {expanded && variants?.map((v) => {
            const key = `${item.name}__${v.name}`;
            const row = sel.flatRows.find((r) => r.key === key);
            if (!row) return null;
            return renderSelectRow(row);
          })}
        </div>
      );
    }

    const row = sel.flatRows.find((r) => r.key === item.name);
    if (!row) return null;
    return renderSelectRow(row);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">{t('items.updatePrices')}</h2>
            <p className="text-sm text-gray-500">{t('items.updatePricesHelp')}</p>
          </div>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="w-80 border-r border-gray-100 flex flex-col shrink-0">
            <div className="p-3 space-y-2 border-b border-gray-100">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  className="input-field pl-9 text-sm"
                  placeholder={t('common.search')}
                  value={sel.search}
                  onChange={(e) => sel.setSearch(e.target.value)}
                />
              </div>
              <select className="input-field text-sm" value={sel.category} onChange={(e) => sel.setCategory(e.target.value)}>
                <option value="All">{t('items.allCategories')}</option>
                {sel.categories.map((c) => (
                  <option key={c.name} value={c.name}>{c.category_name}</option>
                ))}
              </select>
              <div className="flex gap-2">
                <button type="button" onClick={() => void sel.selectAllVisible()} className="btn-secondary text-xs flex-1 py-2">
                  {t('items.selectAll')}
                </button>
                <button type="button" onClick={sel.clearSelection} className="btn-secondary text-xs flex-1 py-2">
                  {t('items.clearSelection')}
                </button>
              </div>
              <p className="text-xs text-gray-500">{t('items.selectedCount', { count: sel.selectedKeys.size })}</p>
            </div>
            <div className="flex-1 overflow-auto">
              {sel.isLoading ? (
                <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
              ) : (
                sel.searchResults.map((item) => renderItemRow(item))
              )}
            </div>
          </div>

          <div className="flex-1 flex flex-col min-w-0">
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex flex-wrap items-end gap-2">
              <div>
                <div className="flex items-center gap-1">
                  <label className="text-xs font-medium text-gray-600">{t('items.bulkRule')}</label>
                  <div className="group relative">
                    <HelpCircle
                      className="w-3.5 h-3.5 text-gray-400 cursor-help"
                      aria-label={t('items.bulkRuleHelpTitle')}
                    />
                    <div className="absolute left-0 top-full mt-1.5 hidden group-hover:block w-80 max-w-[min(20rem,calc(100vw-2rem))] bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 z-30 pointer-events-none">
                      <p className="font-semibold text-gray-100 mb-2">{t('items.bulkRuleHelpTitle')}</p>
                      <ul className="space-y-1.5">
                        {BULK_RULE_HELP_KEYS.map(({ labelKey, helpKey }) => (
                          <li key={helpKey} className="leading-snug">
                            <span className="font-medium text-gray-200">{t(labelKey)}:</span>{' '}
                            <span className="text-gray-300">{t(helpKey)}</span>
                          </li>
                        ))}
                      </ul>
                      <div className="absolute left-3 -top-1.5 w-3 h-3 bg-gray-900 rotate-45" />
                    </div>
                  </div>
                </div>
                <select className="input-field text-sm mt-1" value={ruleType} onChange={(e) => setRuleType(e.target.value as typeof ruleType)}>
                  <option value="percent">{t('items.rulePercent')}</option>
                  <option value="amount">{t('items.ruleAmount')}</option>
                  <option value="set">{t('items.ruleSet')}</option>
                  <option value="margin">{t('items.ruleMargin')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">{t('items.ruleValue')}</label>
                <input className="input-field text-sm mt-1 w-24" value={ruleValue} onChange={(e) => setRuleValue(e.target.value)} />
              </div>
              {(ruleType === 'percent' || ruleType === 'amount') && (
                <div>
                  <label className="text-xs font-medium text-gray-600">{t('items.calculateFrom', 'Calculate from')}</label>
                  <select
                    className="input-field text-sm mt-1 w-36"
                    value={ruleBaseField}
                    onChange={(e) => setRuleBaseField(e.target.value as RuleBaseField)}
                  >
                    <option value="sale">{t('items.retailPrice')}</option>
                    <option value="current">{t('items.currentField', 'Current Field')}</option>
                    <option value="cost">{t('items.costPrice')}</option>
                    <option value="wholesale">{t('items.wholesalePrice')}</option>
                    <option value="reseller">{t('items.resellerPrice')}</option>
                    <option value="minimum">{t('items.minimumPrice')}</option>
                    {showDeliveryPrice && <option value="delivery">{t('items.deliveryPrice')}</option>}
                  </select>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-600">{t('items.applyToPrice')}</label>
                <select className="input-field text-sm mt-1 w-36" value={ruleTarget} onChange={(e) => setRuleTarget(e.target.value as typeof ruleTarget)}>
                  <option value="sale">{t('items.retailPrice')}</option>
                  <option value="wholesale">{t('items.wholesalePrice')}</option>
                  <option value="reseller">{t('items.resellerPrice')}</option>
                  <option value="minimum">{t('items.minimumPrice')}</option>
                  {showDeliveryPrice && <option value="delivery">{t('items.deliveryPrice')}</option>}
                  <option value="all">{t('items.allPrices')}</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">{t('items.roundTo')}</label>
                <select className="input-field text-sm mt-1 w-24" value={roundToStep} onChange={(e) => setRoundToStep(e.target.value)}>
                  <option value="0">{t('items.noRounding')}</option>
                  <option value="0.05">0.05</option>
                  <option value="0.10">0.10</option>
                  <option value="0.50">0.50</option>
                  <option value="1">1.00</option>
                </select>
              </div>
              <button type="button" onClick={applyRule} disabled={ruleApplyKeys.size === 0} className="btn-secondary text-sm py-2">
                {t('items.applyRule')}
              </button>
            </div>

            <div className="flex-1 overflow-auto">
              {sel.selectedRows.length === 0 ? (
                <p className="text-center text-gray-400 py-12 text-sm">{t('items.selectProductsToEdit')}</p>
              ) : (
                <>
                  <div className={`grid ${editGridCols} gap-2 px-3 py-2 border-b border-gray-200 bg-gray-50 sticky top-0 z-10 items-center`}>
                    <input
                      type="checkbox"
                      checked={allRuleApplyChecked}
                      ref={(el) => {
                        if (el) el.indeterminate = someRuleApplyChecked && !allRuleApplyChecked;
                      }}
                      onChange={toggleAllRuleApply}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      aria-label={t('items.selectAll')}
                    />
                    <span className="text-xs font-semibold text-gray-600 uppercase">{t('items.product')}</span>
                    {priceFields.map((field) => (
                      <span key={field} className="text-xs font-semibold text-gray-600 uppercase truncate">
                        {priceFieldLabel(field)}
                      </span>
                    ))}
                  </div>
                  {sel.selectedRows.map((row) => renderEditRow(row))}
                </>
              )}
            </div>

            {showReview && changedRows.length > 0 && (
              <div className="border-t border-amber-200 bg-amber-50 p-3 max-h-32 overflow-auto text-xs">
                {changedRows.map((row) => (
                  <div key={row.key} className="py-0.5">
                    <span className="font-medium truncate block">{row.label}</span>
                    {priceFields.filter((f) => {
                      const e = edits[row.key];
                      return e?.[f] != null && e[f] !== getOldPrice(row, f);
                    }).map((f) => (
                      <div key={f} className="flex justify-between gap-2 pl-2 text-gray-600">
                        <span>{priceFieldLabel(f)}</span>
                        <span>{formatMoney(getOldPrice(row, f))} → {formatMoney(getNewPrice(row, edits, f))}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}

            <div className="p-4 border-t border-gray-100 flex items-center justify-between gap-3">
              <button type="button" onClick={() => setShowReview((v) => !v)} className="btn-secondary text-sm flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {t('items.reviewChanges')} ({changedRows.length})
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saveMutation.isPending || changedRows.length === 0}
                className="btn-primary flex items-center gap-2"
              >
                {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {t('items.savePriceChanges', { count: changedRows.length })}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-full bg-amber-100 text-amber-600 shrink-0">
                <AlertTriangle className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  {t('items.confirmLargePriceChangeTitle', 'Confirm Price Changes')}
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {t('items.confirmLargePriceChange')}
                </p>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => setShowConfirmModal(false)}
                className="btn-secondary text-sm py-2 px-4"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowConfirmModal(false);
                  saveMutation.mutate();
                }}
                className="btn-primary text-sm py-2 px-4"
              >
                {t('common.confirm', 'Continue')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
